/**
 * localStorage caching utilities for low-bandwidth environments.
 * Provides persistent cache that survives page refreshes and browser restarts.
 */

const STORAGE_PREFIX = "rah_"; // RenuAirHub prefix

/**
 * Save data to localStorage with timestamp
 * @param {string} key - Cache key
 * @param {*} data - Data to cache
 */
export const saveToCache = (key, data) => {
  const write = () => {
    try {
      const entry = {
        data,
        timestamp: Date.now(),
      };
      localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(entry));
    } catch (e) {
      // localStorage full or unavailable — silently fail
      console.warn("Cache write failed:", e.message);
    }
  };

  // Defer heavy JSON.stringify + localStorage write off the main thread's critical path
  if (typeof requestIdleCallback === "function") {
    requestIdleCallback(write, { timeout: 2000 });
  } else {
    setTimeout(write, 0);
  }
};

/**
 * Load data from localStorage if still fresh
 * @param {string} key - Cache key
 * @param {number} maxAge - Maximum age in milliseconds
 * @returns {Object|null} { data, timestamp } or null if expired/missing
 */
export const loadFromCache = (key, maxAge) => {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return null;

    const entry = JSON.parse(raw);
    if (Date.now() - entry.timestamp > maxAge) {
      localStorage.removeItem(STORAGE_PREFIX + key);
      return null;
    }

    return entry;
  } catch (e) {
    return null;
  }
};

/**
 * Clear all RenuAirHub cache entries
 */
export const clearCache = () => {
  try {
    const keys = Object.keys(localStorage).filter((k) =>
      k.startsWith(STORAGE_PREFIX),
    );
    keys.forEach((k) => localStorage.removeItem(k));
  } catch (e) {
    // ignore
  }
};
